import React, { useState } from "react";
import MessagingDispatcher from "dispatchers/messaging-dispatcher";
import PricingDispatcher from "dispatchers/pricing-gate-dispatcher";
import { ContentState } from "draft-js";
import { stateToHTML } from "draft-js-export-html";
import { Col, Row } from "react-flexbox-grid";
import * as ReactRedux from "react-redux";
import COLOURS from "util/colours";
import { useBrandBlockStatus } from "util/hooks/useBrandBlockStatus";
import MessagingBrandLogo from "visual-components/messaging/messaging-brand-logo";
import RichEditor from "visual-components/richEditor/RichEditor";

/**
 * Component for rendering the messaging response area, including the rich text editor and quick reply options.
 * @param {Object} props - The component props.
 * @param {string} props.conversationId - The ID of the current conversation. Use "newBrandConversation" for new conversations.
 * @param {string} props.newConversationBrandId - The brand ID for new conversations.
 * @param {string} props.targetBrandId - The brand ID of the target brand in the conversation.
 * @param {boolean} props.showQuickReply - Whether to show quick reply options.
 * @param {function} props.collapse - Function to collapse the conversation view.
 * @returns {import("react").ReactElement} The rendered component.
 */
function MessagingResponse(props) {
  const { conversationId, newConversationBrandId, targetBrandId, collapse } = props;

  const [showQuickReply, setShowQuickReply] = useState(props.showQuickReply);
  const [initialContent, setInitialContent] = useState(null);

  // Use the hook to check if the target brand is blocked
  // For new conversations, use newConversationBrandId; for existing ones, use targetBrandId
  const brandIdToCheck =
    conversationId === "newBrandConversation" ? newConversationBrandId : targetBrandId;
  const { isBlocked } = useBrandBlockStatus(brandIdToCheck);

  const currentBrand = store.getState().currentBrand;

  const save = (contentState) => {
    if (!contentState.hasText()) {
      return;
    }

    if (conversationId === "newBrandConversation") {
      PricingDispatcher.check("messaging.message.create", function () {
        MessagingDispatcher.newBrandConversation({
          brandId: newConversationBrandId,
          content: stateToHTML(contentState),
        });
      });
    } else {
      MessagingDispatcher.sendMessage({
        conversationId: conversationId,
        content: stateToHTML(contentState),
      });
    }
  };

  const populateEditor = (e) => {
    setShowQuickReply(false);
    setInitialContent(ContentState.createFromText(e.target.textContent));
  };

  const showEmptyEditor = () => {
    setShowQuickReply(false);
  };

  return (
    <>
      <Row
        style={{
          paddingTop: "12px",
          paddingRight: "12px",
          paddingBottom: "18px",
          paddingLeft: "12px",
          borderTop: `1px solid ${COLOURS.silver}`,
        }}
      >
        <Col xs={1}>
          <MessagingBrandLogo logo={currentBrand.logo} />
        </Col>
        <Col
          xs={2}
          style={{
            paddingLeft: "12px",
          }}
        >
          <p
            style={{
              fontFamily: "Larsseit-Bold",
              fontSize: "14px",
              marginTop: "8px",
            }}
          >
            {currentBrand.accountname}
          </p>
          <p
            style={{
              fontFamily: "Larsseit",
              fontSize: "12px",
              marginTop: "12px",
            }}
          >
            {"me"}
          </p>
        </Col>
        {conversationId !== "newBrandConversation" && (
          <Col
            xs={9}
            style={{
              textAlign: "right",
            }}
          >
            <div onClick={collapse}>
              <p
                style={{
                  color: COLOURS.azure,
                  fontSize: "14px",
                  fontFamily: "Larsseit-Medium",
                  marginTop: "8px",
                  display: "inline-block",
                  cursor: "pointer",
                }}
              >
                Collapse
              </p>
              <img
                src="/images/icons/messaging-icons/industry-list-collapse-arrow.svg"
                style={{
                  width: "12px",
                  marginLeft: "8px",
                  marginRight: "8px",
                  cursor: "pointer",
                }}
              />
            </div>
          </Col>
        )}
      </Row>
      <Row>
        <Col
          xsOffset={1}
          xs={11}
          style={{
            paddingTop: "0px",
            paddingRight: "25px",
            paddingBottom: "16px",
            paddingLeft: "25px",
          }}
        >
          {showQuickReply ? (
            <div>
              <p
                onClick={showEmptyEditor}
                style={{
                  fontFamily: "Larsseit-Medium",
                  fontSize: "14px",
                  color: COLOURS.azure,
                  marginBottom: "30px",
                  cursor: "pointer",
                }}
              >
                Write Own Reply
              </p>
              <p>
                <span
                  onClick={populateEditor}
                  style={{
                    color: COLOURS.azure,
                    fontFamily: "Larsseit-Medium",
                    fontSize: "14px",
                    marginRight: "24px",
                    cursor: "pointer",
                  }}
                >
                  Yes let's chat
                </span>
                <span
                  onClick={populateEditor}
                  style={{
                    color: COLOURS.azure,
                    fontFamily: "Larsseit-Medium",
                    fontSize: "14px",
                    marginRight: "24px",
                    cursor: "pointer",
                  }}
                >
                  Maybe later
                </span>
                <span
                  onClick={populateEditor}
                  style={{
                    color: COLOURS.azure,
                    fontFamily: "Larsseit-Medium",
                    fontSize: "14px",
                    marginRight: "24px",
                    cursor: "pointer",
                  }}
                >
                  No Thanks
                </span>
              </p>
            </div>
          ) : (
            <RichEditor
              buttonText="Send Message"
              buttonClick={save}
              styleOptions={["Bold", "Italic", "UL"]}
              initialContent={initialContent}
              disabled={isBlocked}
            />
          )}
        </Col>
      </Row>
    </>
  );
}

const mapState = (state) => {
  return {
    newConversationBrandId: state.conversations.newConversationBrandId,
  };
};
export default ReactRedux.connect(mapState)(MessagingResponse);
